import { resolveDrivers } from './drivers'
import { kassaEnabled } from './kassas'
import { linkPhase } from '@krp/client-runtime'
import type { CandidateState, DriverCategory, DriverStatus, EquipmentCandidate, EquipmentSnapshot, ScanRun } from './types'

/**
 * Сканирование (п. 4): автоопределение кассы и терминалов. Окно сканирует по драйверам отдельными запросами;
 * здесь — только решения и представление: когда сканировать само, что показать про найденное, какое действие дать.
 */

/** Предел ожидания ответа одного драйвера, мс (агент отвечает за 25 с, окно ждёт 30). */
export const SCAN_DRIVER_LIMIT_MS = 30_000

/** Кандидат-подсказка «касса по COM?»: не устройство, а список портов; в списке найденного его нет. */
const COM_HINT_KIND = 'atol-com-hint'

export const CANDIDATE_WORDS: Record<CandidateState, { word: string; glyph: '✓' | '!' | '✕' | '–' }> = {
  ready: { word: 'Готов к подключению', glyph: '✓' },
  'needs-input': { word: 'Нужно ввести', glyph: '!' },
  'no-device': { word: 'Устройство не отвечает', glyph: '✕' },
  busy: { word: 'Занято другой программой', glyph: '!' },
  'driver-missing': { word: 'Драйвер не установлен', glyph: '✕' },
  'service-stopped': { word: 'Драйвер не запущен', glyph: '!' },
  unsupported: { word: 'Не поддерживается', glyph: '–' },
  error: { word: 'Ошибка', glyph: '✕' },
}

/** Действие над найденным: что нажать (таблица п. 4.3). */
export type CandidateAction = 'connect' | 'replace' | 'rescan' | 'download-driver' | null

export interface CandidateVm {
  id: string
  kind: string
  category: DriverCategory
  title: string
  connection: string
  word: string
  glyph: '✓' | '!' | '✕' | '–'
  /** Текст агента дословно (ADR-8). */
  text: string
  configured: boolean
  /** Модель, номер и ФФД (только касса). */
  deviceLine: string | null
  action: CandidateAction
  /** Поля, которые надо дописать руками: терминал — по форме драйвера, касса по COM — выбор порта. */
  inputs: readonly { key: string; title: string; hint: string | null }[]
  needsComPort: boolean
  suggested: Readonly<Record<string, string>>
}

export interface ScanRowVm {
  driverId: string
  title: string
  state: ScanRun['state']
  /** «идёт, до 25 с», «найден 1», «не ответил за 25 секунд». */
  text: string
}

export interface ScanVm {
  category: DriverCategory
  running: boolean
  canStart: boolean
  /** Почему кнопка «Найти …» выключена. */
  disabledReason: string | null
  rows: readonly ScanRowVm[]
  candidates: readonly CandidateVm[]
  /** Итоговая строка, когда скан закончен, а подключать нечего. */
  summary: string | null
  comPorts: readonly string[]
  /** Ошибки отдельных драйверов (не скрывают результаты других). */
  errors: readonly string[]
  hints: readonly string[]
  lastScanAt: number | null
}

const EMPTY_HINTS: Record<DriverCategory, readonly string[]> = {
  'fiscal-register': ['Проверьте кабель и питание кассы.', 'Убедитесь, что установлен драйвер ККТ 10.', 'Касса по COM-порту? Выберите порт и нажмите «Проверить порт».'],
  'payment-terminal': ['Проверьте, что терминал включён и подключён.', 'Убедитесь, что драйвер терминала установлен и запущен.'],
  other: [],
}

export const runsOf = (snapshot: EquipmentSnapshot, category: DriverCategory): readonly ScanRun[] =>
  Object.values(snapshot.scans).filter((run) => run.category === category)

export const scanRunning = (snapshot: EquipmentSnapshot, category: DriverCategory): boolean =>
  runsOf(snapshot, category).some((run) => run.state === 'running')

function driversOf(snapshot: EquipmentSnapshot, category: DriverCategory): readonly DriverStatus[] {
  return resolveDrivers(snapshot).filter((driver) => driver.category === category)
}

/** Настроено ли в категории хоть одно устройство (тогда само ничего не ищем). */
export function categoryConfigured(snapshot: EquipmentSnapshot, category: DriverCategory): boolean {
  if (category === 'fiscal-register') return kassaEnabled(snapshot)
  if (category === 'payment-terminal') return (snapshot.terminals?.items.length ?? 0) > 0
  return false
}

/** Драйвер категории готов (хотя бы один). */
export const categoryDriverReady = (snapshot: EquipmentSnapshot, category: DriverCategory): boolean =>
  driversOf(snapshot, category).some((driver) => driver.state === 'ready')

/**
 * Пора ли запустить автоскан категории (п. 4.1): один раз, если агент на связи, драйвер готов, устройств в категории нет
 * и скана в этой сессии ещё не было. Никогда — при `lost`/`outdated`/`denied`, при настроенном устройстве, без драйвера.
 */
export function autoScanDue(snapshot: EquipmentSnapshot, category: DriverCategory): boolean {
  if (category === 'other') return false
  if (linkPhase(snapshot.link, snapshot.now) !== 'connected') return false
  if (!categoryDriverReady(snapshot, category)) return false
  if (categoryConfigured(snapshot, category)) return false
  if (snapshot.autoScanned.includes(category)) return false
  if (runsOf(snapshot, category).length > 0) return false
  return true
}

/** Драйвер категории только что стал готовым (поставили — окно заметило): повод для одного автоскана после установки. */
export function driverBecameReady(before: readonly DriverStatus[] | null, after: readonly DriverStatus[], category: DriverCategory): boolean {
  const wasReady = (before ?? []).some((driver) => driver.category === category && driver.state === 'ready')
  const isReady = after.some((driver) => driver.category === category && driver.state === 'ready')
  return !wasReady && isReady && before !== null
}

/** Подпись вместо «ждём»: сколько секунд ещё можно ждать драйвер. */
export const SCAN_RUNNING_TEXT = 'идёт, до 25 с'

function rowText(run: ScanRun, driverTitle: string): string {
  switch (run.state) {
    case 'running':
      return SCAN_RUNNING_TEXT
    case 'cancelled':
      return 'отменено'
    case 'failed':
      return run.error ?? `${driverTitle}: не ответил за 25 секунд`
    case 'done': {
      const count = run.candidates.filter((candidate) => candidate.kind !== COM_HINT_KIND).length
      return count === 0 ? 'ничего не найдено' : `найдено: ${count}`
    }
    case 'idle':
      return ''
  }
}

function candidateActionOf(candidate: EquipmentCandidate): CandidateAction {
  if (candidate.configured) return null
  switch (candidate.state) {
    case 'ready':
    case 'needs-input':
      return 'connect'
    case 'no-device':
    case 'busy':
    case 'service-stopped':
      return 'rescan'
    case 'driver-missing':
      return 'download-driver'
    case 'unsupported':
    case 'error':
      return null
  }
}

function deviceLine(candidate: EquipmentCandidate): string | null {
  const device = candidate.device
  if (device === null) return null
  const parts = [
    device.modelName ?? (device.model === null ? null : `модель ${device.model}`),
    device.serial === null ? null : `№ ${device.serial}`,
    device.ffdVersion === null ? null : `ФФД ${device.ffdVersion}`,
  ].filter((part): part is string => part !== null)
  return parts.length === 0 ? null : parts.join(' · ')
}

/** Строка найденного. `replaceKassa` — касса уже есть или это не первый кандидат-касса: подпись «Заменить кассу». */
export function candidateVm(candidate: EquipmentCandidate, snapshot: EquipmentSnapshot, replaceKassa: boolean): CandidateVm {
  const words = candidate.configured ? { word: 'Подключено', glyph: '✓' as const } : CANDIDATE_WORDS[candidate.state]
  let action = candidateActionOf(candidate)
  if (action === 'connect' && candidate.category === 'fiscal-register' && replaceKassa) action = 'replace'
  const driver = snapshot.terminals?.drivers.find((item) => item.kind === candidate.kind)
  const canInput = action === 'connect' || action === 'replace'
  const inputs =
    canInput && candidate.category === 'payment-terminal'
      ? (driver?.fields ?? [])
          .filter((field) => field.required && (candidate.suggested[field.key] ?? '').length === 0)
          .map((field) => ({ key: field.key, title: field.title, hint: field.hint }))
      : []
  return {
    id: candidate.candidateId,
    kind: candidate.kind,
    category: candidate.category,
    title: candidate.title,
    connection: candidate.connection,
    word: words.word,
    glyph: words.glyph,
    text: candidate.message ?? '',
    configured: candidate.configured,
    deviceLine: deviceLine(candidate),
    action,
    inputs,
    needsComPort: canInput && candidate.category === 'fiscal-register' && candidate.state === 'needs-input',
    suggested: candidate.suggested,
  }
}

/** Блок «Сканирование» вкладки категории. */
export function scanVm(snapshot: EquipmentSnapshot, category: DriverCategory): ScanVm {
  const runs = runsOf(snapshot, category)
  const running = scanRunning(snapshot, category)
  const phase = linkPhase(snapshot.link, snapshot.now)
  const drivers = driversOf(snapshot, category)

  let disabledReason: string | null = null
  if (phase !== 'connected') disabledReason = 'Нет связи с агентом'
  else if (running) disabledReason = 'Сканирование уже идёт'
  else if (!categoryDriverReady(snapshot, category)) {
    disabledReason = category === 'fiscal-register' ? 'Сначала установите драйвер ККТ 10' : 'Сначала установите драйвер терминала'
  }

  const comPorts = [...new Set(runs.flatMap((run) => run.hints.comPorts))]
  const all = runs.flatMap((run) => run.candidates)
  const listed = all.filter((candidate) => candidate.kind !== COM_HINT_KIND)
  const firstFiscalConnectable = listed.findIndex((candidate) => candidate.category === 'fiscal-register' && !candidate.configured && (candidate.state === 'ready' || candidate.state === 'needs-input'))
  const candidates = listed.map((candidate, index) =>
    candidateVm(candidate, snapshot, kassaEnabled(snapshot) || (firstFiscalConnectable >= 0 && index > firstFiscalConnectable)),
  )
  const rows = runs.map((run): ScanRowVm => {
    const title = drivers.find((driver) => driver.id === run.driverId)?.title ?? run.driverId
    return { driverId: run.driverId, title, state: run.state, text: rowText(run, title) }
  })
  const finished = runs.length > 0 && runs.every((run) => run.state !== 'running' && run.state !== 'idle')
  const nothingToConnect = !candidates.some((candidate) => candidate.action !== null || candidate.configured)
  const errors = runs.flatMap((run) => (run.state === 'failed' && run.error !== null ? [run.error] : []))
  return {
    category,
    running,
    canStart: disabledReason === null,
    disabledReason,
    rows,
    candidates,
    summary:
      finished && candidates.length === 0
        ? category === 'fiscal-register'
          ? 'Ничего не найдено. Проверьте подключение и нажмите «Найти кассу» ещё раз.'
          : 'Ничего не найдено. Проверьте подключение и нажмите «Найти терминал» ещё раз.'
        : finished && nothingToConnect
          ? 'Подключать нечего: посмотрите советы выше.'
          : null,
    comPorts,
    errors,
    hints: finished && candidates.length === 0 ? EMPTY_HINTS[category] : [],
    lastScanAt: runs.reduce<number | null>((latest, run) => (run.finishedAt !== null && (latest === null || run.finishedAt > latest) ? run.finishedAt : latest), null),
  }
}

/** Есть ли готовое к подключению (число на табе «Терминалы» и вердикт). */
export function hasConnectableCandidate(snapshot: EquipmentSnapshot, category: DriverCategory): boolean {
  return runsOf(snapshot, category)
    .flatMap((run) => run.candidates)
    .some((candidate) => !candidate.configured && (candidate.state === 'ready' || candidate.state === 'needs-input') && candidate.kind !== COM_HINT_KIND)
}
